/**
 * Typed time-entry fetch shared by report hooks (browser, RLS) and cron jobs
 * (service client). Selects only denormalized scalar columns — no joins.
 *
 * The caller supplies the Supabase client so the SAME query logic serves both
 * surfaces: the page passes an RLS browser client, the cron passes the
 * service-role client. Both are `SupabaseClient<Database>`, so the `.select`
 * result is fully typed against the generated schema.
 */
import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/lib/supabase/database.types";
import {
  REPORT_TIME_ENTRY_CLIENT_SELECT,
  REPORT_TIME_ENTRY_SELECT,
  type ReportClientTimeEntryRow,
  type ReportTimeEntryRow,
} from "./time-entry-fields";

export interface ReportTimeEntryFilters {
  /** Inclusive ISO date (YYYY-MM-DD) lower bound on `date_worked`. */
  start: string;
  /** Inclusive ISO date (YYYY-MM-DD) upper bound on `date_worked`. */
  end: string;
  /** Restrict to one company (used for client-portal scoping + per-company billing). */
  companyId?: string;
  /** Restrict to one employee. */
  userId?: string;
  /** When true, only `is_approved = true` entries (the canonical billed/payable set). */
  approvedOnly?: boolean;
}

const PAGE_SIZE = 1000;

/**
 * Fetch all time entries in a date range, paginated to avoid PostgREST's
 * default row cap. Returns codegen-typed rows. Throws on query error so callers
 * surface a real failure rather than a silent empty report.
 */
export async function fetchReportTimeEntries(
  supabase: SupabaseClient<Database>,
  filters: ReportTimeEntryFilters
): Promise<ReportTimeEntryRow[]> {
  const rows: ReportTimeEntryRow[] = [];

  for (let from = 0; ; from += PAGE_SIZE) {
    let query = supabase
      .from("time_entries")
      .select(REPORT_TIME_ENTRY_SELECT)
      .gte("date_worked", filters.start)
      .lte("date_worked", filters.end)
      .order("date_worked", { ascending: false })
      .range(from, from + PAGE_SIZE - 1);

    if (filters.companyId) query = query.eq("company_id", filters.companyId);
    if (filters.userId) query = query.eq("user_id", filters.userId);
    if (filters.approvedOnly) query = query.eq("is_approved", true);

    const { data, error } = await query;
    if (error) {
      throw new Error(`Failed to fetch report time entries: ${error.message}`);
    }

    // Trust boundary: PostgREST can't infer column types from a runtime-built
    // select string. The projection is provably a subset of `time_entries`
    // (REPORT_TIME_ENTRY_FIELDS `satisfies (keyof Tables<"time_entries">)[]`),
    // so this single boundary cast is sound and replaces the old per-row `any`
    // + nested-relation `unwrapRelation` shims.
    const batch = (data ?? []) as unknown as ReportTimeEntryRow[];
    rows.push(...batch);
    if (batch.length < PAGE_SIZE) break;
  }

  return rows;
}

/**
 * CLIENT-SAFE variant of {@link fetchReportTimeEntries} (role = client_user).
 * Identical filtering, but selects ONLY the work-transparency columns
 * (`REPORT_TIME_ENTRY_CLIENT_SELECT`) so internal columns (`billable_rate`,
 * `user_id`/`user_name`, `is_approved`, `sync_status`, …) never reach a client
 * over the wire. RLS still scopes the rows; this enforces the column boundary.
 * Use this for any report read on a client-portal surface.
 */
export async function fetchClientReportTimeEntries(
  supabase: SupabaseClient<Database>,
  filters: ReportTimeEntryFilters
): Promise<ReportClientTimeEntryRow[]> {
  const rows: ReportClientTimeEntryRow[] = [];

  for (let from = 0; ; from += PAGE_SIZE) {
    let query = supabase
      .from("time_entries")
      .select(REPORT_TIME_ENTRY_CLIENT_SELECT)
      .gte("date_worked", filters.start)
      .lte("date_worked", filters.end)
      .order("date_worked", { ascending: false })
      .range(from, from + PAGE_SIZE - 1);

    if (filters.companyId) query = query.eq("company_id", filters.companyId);
    if (filters.userId) query = query.eq("user_id", filters.userId);
    if (filters.approvedOnly) query = query.eq("is_approved", true);

    const { data, error } = await query;
    if (error) {
      throw new Error(`Failed to fetch client report time entries: ${error.message}`);
    }

    const batch = (data ?? []) as unknown as ReportClientTimeEntryRow[];
    rows.push(...batch);
    if (batch.length < PAGE_SIZE) break;
  }

  return rows;
}
